import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import type { Editor, JSONContent } from '@tiptap/core';
import { BerryLoader } from '@web-app/components/BerryLoader';
import { useDailyRoom } from '@web-app/components/CallV2/hooks/useDailyRoom';
import { useInSessionTemplateSource } from '@web-app/components/ChartingNoteEditor/hooks/useInSessionTemplateSource';
import { TemplateSelector } from '@web-app/components/ChartingNoteEditor/TemplateSelector';
import { EditorName, TipTapToolbar } from '@web-app/components/TipTap';
import { SaveStatusBadge } from '@web-app/components/TipTap/SaveStatusBadge';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useSessionNote } from '@web-app/hooks/useSessionNote';
import { useOrganizationConfig } from '@web-app/hooks/whitelabel/useOrganizationConfig';
import { Note } from '@web-app/server/api/types';
import { getSessionNoteData } from '@web-app/utils/api/session-notes/utils';
import debounce from 'debounce';
import dynamic from 'next/dynamic';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';

import { useAutoSaveWithStatus } from './hooks/useAutoSaveWithStatus';

const LOG_PREFIX = 'TipTapTab';

// Dynamically import TipTapEditor to avoid SSR issues
const TipTapEditor = dynamic(
  () =>
    import('@web-app/components/TipTap/TipTapEditor').then((mod) => ({
      default: mod.TipTapEditor,
    })),
  {
    ssr: false,
    loading: () => <BerryLoader height="200px" />,
  },
);

interface TipTapTabProps {
  appointment?: AppointmentResponse;
}

/**
 * TipTap tab component for the side panel
 * Provides a WYSIWYG editor for notes using TipTap
 */
export const TipTapTab: FC<TipTapTabProps> = ({ appointment }) => {
  const { roomId } = useDailyRoom();

  // Use Jotai atom for session note state with built-in fetching
  // The atom now owns both state AND fetching logic
  // roomId is automatically derived from URL via jotai-location
  const {
    sessionNote,
    setSessionNote,
    isLoading: isLoadingSessionNote,
  } = useSessionNote();

  const config = useOrganizationConfig();
  const organizationId = config?.id?.toString();
  const { trackEvent } = useAnalytics();
  const {
    templates: chartingNotesTemplates,
    resolveTemplate,
    isLoading: isLoadingTemplates,
  } = useInSessionTemplateSource();

  // Create a stable key that only changes when template content actually changes
  const templateKey = useMemo(
    () =>
      chartingNotesTemplates
        .map((t) => `${t.title}:${t.editor || 'quill'}`)
        .join(','),
    [chartingNotesTemplates],
  );

  // Combine loading states - wait for templates (which includes org) and session note
  // Only show loading when roomId exists to prevent false loading states during router initialization
  const isLoading = (isLoadingSessionNote || isLoadingTemplates) && !!roomId;

  // Log filtered templates in TipTapTab context for debugging template selection issues
  // Only fires when the actual template list changes, not on every render
  useEffect(() => {
    if (chartingNotesTemplates.length > 0 && appointment) {
      datadogLogs.logger.info(
        `${LOG_PREFIX} - Filtered templates loaded in TipTapTab`,
        {
          organizationId,
          roomId,
          providerId: appointment.provider_id,
          clientId: appointment.client_id,
          filteredTemplatesCount: chartingNotesTemplates.length,
          filteredTemplateTitles: chartingNotesTemplates.map((t) => ({
            title: t.title,
            editor: t.editor || 'quill',
          })),
        },
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]); // Only depends on template content, not appointment/room changes

  const [editorContent, setEditorContent] = useState<string | object>(
    sessionNote?.sessionNote?.providerNotes || '',
  );
  const [hasContent, setHasContent] = useState<boolean>(
    !!sessionNote?.sessionNote?.providerNotes,
  );

  const [contentKey, setContentKey] = useState<number>(0);
  const [isSwitchTemplateDrawerOpen, setIsSwitchTemplateDrawerOpen] =
    useState(false);
  const [hasLoadedInitialContent, setHasLoadedInitialContent] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);

  // Track when we're actively switching templates to prevent race conditions
  // with auto-save responses overwriting the new template content
  const isSwitchingTemplateRef = useRef(false);

  // Track first edit for analytics
  const hasEditedRef = useRef(false);

  const {
    saveStatus,
    lastSaveError,
    saveImmediateWithStatus,
    saveProviderNotes,
    handleSaveSuccess,
    handleSaveError,
    setSaveStatus,
  } = useAutoSaveWithStatus({
    roomId,
    appointment,
    sessionNote,
    setSessionNote,
    organizationId,
    logPrefix: LOG_PREFIX,
  });

  // Clear the template switching flag when save completes
  useEffect(() => {
    if (
      isSwitchingTemplateRef.current &&
      (saveStatus === 'saved' || saveStatus === 'error')
    ) {
      // Clear the flag after save completes to allow normal content updates
      isSwitchingTemplateRef.current = false;
    }
  }, [saveStatus]);

  // Load initial content from sessionNote atom on mount
  // After initial load, component controls the atom via handleContentChange
  useEffect(() => {
    // Only load content once on initial mount
    // After that, component controls the atom via handleContentChange
    if (hasLoadedInitialContent || isSwitchingTemplateRef.current) {
      return;
    }

    const providerNotes = sessionNote?.sessionNote?.providerNotes;
    const providerNotesJson = sessionNote?.sessionNote?.providerNotesJson;
    const contentToLoad = providerNotesJson || providerNotes;

    if (contentToLoad === undefined) {
      return;
    }

    const hasProviderNotes =
      !!contentToLoad &&
      (typeof contentToLoad === 'string' ? contentToLoad.trim() !== '' : true);

    // Load content and mark as loaded
    if (hasProviderNotes) {
      setEditorContent(contentToLoad);
      setHasContent(true);
      setHasLoadedInitialContent(true);
    }
  }, [
    sessionNote?.sessionNote?.providerNotes,
    sessionNote?.sessionNote?.providerNotesJson,
    hasLoadedInitialContent,
  ]);

  // Auto-create session note if it doesn't exist
  useEffect(() => {
    if (
      sessionNote &&
      !sessionNote?.sessionNote?.id &&
      appointment?.provider_id &&
      appointment?.client_id
    ) {
      saveImmediateWithStatus(
        getSessionNoteData({
          sessionNote,
          appointment,
          roomId,
        }),
      );
    }
  }, [
    sessionNote?.sessionNote?.id,
    appointment?.provider_id,
    appointment?.client_id,
    saveImmediateWithStatus,
    roomId,
    sessionNote,
    appointment,
  ]);

  // Debounced API save - only debounce the network call, not atom updates
  // This ensures the atom is always up-to-date while still batching API calls
  // Keep this stable by not including callbacks in dependencies
  const debouncedApiSave = useMemo(
    () =>
      debounce(
        (
          updatedNotes: Note,
          onSuccess: () => void,
          onError: (error: Error) => void,
        ) => {
          // The atom is already updated at this point, so we're just persisting to the server
          saveProviderNotes(updatedNotes, {
            onSuccess,
            onError,
          });
        },
        2000,
      ),
    [saveProviderNotes],
  );

  // Cleanup: cancel pending API saves on unmount (don't flush to avoid race conditions)
  useEffect(() => {
    return () => {
      debouncedApiSave.clear();
    };
  }, [debouncedApiSave]);

  const handleContentChange = useCallback(
    (content: string, contentJson?: JSONContent) => {
      // Track first edit for analytics
      if (!hasEditedRef.current) {
        hasEditedRef.current = true;
        trackEvent(EVENT_NAMES.EDITS_CHARTING_NOTE, {
          roomId,
          appointmentType: appointment?.appointmentType?.name,
          clientId: appointment?.client_id,
          organizationId,
        });
        datadogLogs.logger.info(`${LOG_PREFIX} - First edit made to note`, {
          roomId,
          organizationId,
          providerId: appointment?.provider_id,
          clientId: appointment?.client_id,
          appointmentType: appointment?.appointmentType?.name,
        });
      }

      setEditorContent(content);
      setHasContent(true);
      setSaveStatus('saving'); // Show saving status immediately

      // Update atom immediately using functional form to get current state
      // This prevents the callback from being recreated on every content change
      setSessionNote((currentSessionNote) => {
        const updatedNotes = getSessionNoteData({
          data: {
            providerNotes: content,
            ...(contentJson ? { providerNotesJson: contentJson } : {}),
          },
          sessionNote: currentSessionNote,
          roomId,
          appointment,
        });

        // Debounce only the API call (performance optimization)
        debouncedApiSave(updatedNotes, handleSaveSuccess, handleSaveError);

        return currentSessionNote
          ? { ...currentSessionNote, ...updatedNotes }
          : (updatedNotes as Note);
      });
    },
    [
      trackEvent,
      organizationId,
      debouncedApiSave,
      roomId,
      appointment,
      setSessionNote,
      setSaveStatus,
      handleSaveSuccess,
      handleSaveError,
    ],
  );

  const handleTemplateChange = useCallback(
    async (selected: string | null, isSwitch: boolean = false) => {
      const templateName = selected || '(no template - start from scratch)';
      const userTags = appointment?.user?.tags ?? [];
      const tagNames = userTags.map((tag) => tag.name);

      trackEvent(EVENT_NAMES.CHARTING_NOTE_TEMPLATE_SELECTED, {
        templateName,
        roomId,
        appointmentType: appointment?.appointmentType?.name,
        appointmentUserTags: tagNames,
        clientId: appointment?.client_id,
        isSwitching: isSwitch,
      });

      if (!selected) {
        // Start new note - set flag to prevent race conditions with auto-save
        isSwitchingTemplateRef.current = true;
        // Reset edit tracking for new note
        hasEditedRef.current = false;

        setEditorContent('');
        setHasContent(true);
        setEditor(null); // Clear editor instance so toolbar doesn't render with stale editor
        setContentKey((prev) => prev + 1);
        setHasLoadedInitialContent(true);

        if (appointment?.provider_id && appointment?.client_id) {
          const updatedNotes = getSessionNoteData({
            data: { providerNotes: '' },
            sessionNote: {
              ...sessionNote,
              sessionNote: sessionNote?.sessionNote
                ? {
                    ...sessionNote.sessionNote,
                    selectedTemplateName: 'empty',
                  }
                : null,
            },
            roomId,
            appointment,
          });
          setSessionNote((s) =>
            s ? { ...s, ...updatedNotes } : (updatedNotes as Note),
          );
          saveImmediateWithStatus(updatedNotes);
          // Flag will be cleared by useEffect when save completes
        } else {
          // If no appointment data, clear flag immediately
          isSwitchingTemplateRef.current = false;
        }

        // Close the switch template drawer after state updates
        if (isSwitch) {
          setIsSwitchTemplateDrawerOpen(false);
        }
        return;
      }

      // Resolve template content (hardcoded lookup or DB fetch by slug; handles
      // both quill and TipTap templates)
      const template = await resolveTemplate(selected);

      if (!template) {
        console.error('[TipTapTab] Template not found:', selected);
        return;
      }

      // Handle template content based on editor type
      let templateContent: string | object;
      if (
        template.editor === 'tiptap' &&
        typeof template.content === 'object'
      ) {
        // TipTap JSON template - deep copy to avoid mutations
        templateContent = JSON.parse(JSON.stringify(template.content));
      } else if (typeof template.content === 'string') {
        // Quill HTML template - TipTap can handle HTML strings
        templateContent = template.content;
      } else {
        console.error('[TipTapTab] Invalid template content:', template);
        return;
      }

      // Set flag to prevent race conditions with auto-save during template switch
      isSwitchingTemplateRef.current = true;
      // Reset edit tracking for new template
      hasEditedRef.current = false;

      setEditorContent(templateContent);
      setHasContent(true);
      setEditor(null); // Clear editor instance so toolbar doesn't render with stale editor
      setContentKey((prev) => prev + 1); // Force remount with new content
      setHasLoadedInitialContent(true);

      // Save template selection with content
      if (appointment?.provider_id && appointment?.client_id && roomId) {
        const updatedNotes = getSessionNoteData({
          data: {
            // Save template content in the appropriate format
            providerNotes:
              typeof templateContent === 'string' ? templateContent : undefined,
            providerNotesJson:
              typeof templateContent === 'object' ? templateContent : undefined,
          },
          sessionNote: {
            ...sessionNote,
            sessionNote: sessionNote?.sessionNote
              ? {
                  ...sessionNote.sessionNote,
                  selectedTemplateName: selected,
                }
              : null,
          },
          roomId,
          appointment,
        });
        setSessionNote((s) =>
          s ? { ...s, ...updatedNotes } : (updatedNotes as Note),
        );
        saveImmediateWithStatus(updatedNotes);
        // Flag will be cleared by useEffect when save completes
      } else {
        // If no appointment data, clear flag immediately
        isSwitchingTemplateRef.current = false;
      }

      datadogLogs.logger.info(`${LOG_PREFIX} - Template loaded`, {
        templateName: selected,
        templateEditor: template.editor || 'quill',
        roomId,
        organizationId,
        providerId: appointment?.provider_id,
        clientId: appointment?.client_id,
      });

      // Close the switch template drawer after all state updates complete
      if (isSwitch) {
        setIsSwitchTemplateDrawerOpen(false);
      }
    },
    [
      appointment,
      roomId,
      sessionNote,
      setSessionNote,
      saveImmediateWithStatus,
      organizationId,
      resolveTemplate,
      trackEvent,
    ],
  );

  return (
    <Flex direction="column" position="relative" data-testid="tiptap-tab">
      {/* Show loading state while fetching org/template or session note data */}
      {isLoading && (
        <Flex justify="center" align="center" h="200px">
          <BerryLoader height="100px" />
        </Flex>
      )}

      {/* Show template selector only after data has loaded and there's no content */}
      {!isLoading && !hasContent && (
        <TemplateSelector
          templates={chartingNotesTemplates}
          onTemplateSelect={handleTemplateChange}
          onStartNewNote={() => handleTemplateChange(null)}
          testIdPrefix="tiptap-template-"
          startNewNoteTestId="start-new-tiptap-note"
        />
      )}

      {/* Show editor only after data has loaded and there's content */}
      {!isLoading && hasContent && (
        <>
          <HStack
            justify="space-between"
            align="center"
            p={4}
            pb={2}
            flexShrink={0}
          >
            <VStack spacing={1} align="start" flex="1">
              <HStack spacing={2} align="center" h="20px">
                <Text fontWeight="600" fontSize="md" lineHeight="20px">
                  Notes
                </Text>
                <SaveStatusBadge status={saveStatus} />
              </HStack>
              {sessionNote?.sessionNote?.selectedTemplateName &&
                sessionNote.sessionNote.selectedTemplateName !== 'empty' && (
                  <Text fontSize="xs" color="text.muted">
                    Template: {sessionNote.sessionNote.selectedTemplateName}
                  </Text>
                )}
            </VStack>
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              leftIcon={<FiRefreshCw />}
              onClick={() => setIsSwitchTemplateDrawerOpen(true)}
              data-testid="switch-template-button"
            >
              Switch Template
            </Button>
          </HStack>
          {lastSaveError && saveStatus === 'error' && (
            <Text fontSize="xs" color="status.error" px={2}>
              {lastSaveError}
            </Text>
          )}
          {/* Toolbar - rendered separately for sticky positioning */}
          {editor && (
            <TipTapToolbar
              editor={editor}
              editable={true}
              isStandalone={true}
            />
          )}
          <TipTapEditor
            name={EditorName.InSessionProviderNotes}
            key={`editor-${contentKey}`}
            content={editorContent}
            onChange={handleContentChange}
            onEditorReady={setEditor}
            showToolbar={false}
            placeholder="Start typing your notes here..."
          />
        </>
      )}

      {/* Switch Template Drawer */}
      <Drawer
        isOpen={isSwitchTemplateDrawerOpen}
        placement="right"
        onClose={() => setIsSwitchTemplateDrawerOpen(false)}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Switch Template</DrawerHeader>

          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="orange.600" fontWeight="500">
                ⚠️ Warning: Switching templates will replace your current
                content.
              </Text>
              <TemplateSelector
                templates={chartingNotesTemplates}
                onTemplateSelect={(templateTitle) =>
                  handleTemplateChange(templateTitle, true)
                }
                onStartNewNote={() => handleTemplateChange(null, true)}
                testIdPrefix="switch-template-"
                startNewNoteTestId="switch-to-new-note"
              />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
};
